import { Injectable } from "@angular/core";
import { ApiService } from "../api/api.service";
import { Observable, of } from "rxjs";
import { map, tap } from "rxjs/operators";
import SearchResponse from "../../response/search.response";
import { PermissionsHelper } from "src/app/helpers/Permission";
import localStore from "store";

@Injectable({
  providedIn: "root",
})
export class ApplicationsService {
  constructor(private api: ApiService, public permission: PermissionsHelper) {}

  public setOnHold(id, data): Observable<any> {
    return this.api.put("/applications/" + id + "/hold_status", {
      body: data,
      headers: {
        Permissions: this.permission.getJWT("applications"),
      },
    });
  }

  public setEOIStatus(id, data): Observable<any> {
    return this.api.put("/applications/" + id + "/eoi_status", {
      body: data,
      headers: {
        Permissions: this.permission.getJWT("applications"),
      },
    });
  }

  public createEjobboAccount(id): Observable<any> {
    return this.api.post("/applications/create-ejobbo/" + id, {
      headers: {
        Permissions: this.permission.getJWT("applications"),
      },
    });
  }

  public setStatus(id, data): Observable<any> {
    return this.api.post("/applications/" + id + "/status", {
      body: data,
      headers: {
        Permissions: this.permission.getJWT("applications"),
      },
    });
  }

  public setImmiStatus(body: {
    lead_number: number | string;
    status: boolean;
  }): Observable<any> {
    return this.api.post("/applications/update-immi-status", {
      body,
      headers: {
        Permissions: this.permission.getJWT("applications"),
      },
    });
  }

  public getList(params): Observable<any> {
    if (params instanceof SearchResponse) {
      params = params.getReqData();
    }

    return this.api.get("/applications/v2", {
      params: params,
      headers: {
        Permissions: this.permission.getJWT("applications"),
      },
    });
  }

  public getAllList(params): Observable<any> {
    if (params instanceof SearchResponse) {
      params = params.getReqData();
    }
    return this.api.get("/applications/tech", {
      params: params,
      headers: {
        Permissions: this.permission.getJWT("applications"),
      },
    });
  }

  public getListRisks(params): Observable<any> {
    if (params instanceof SearchResponse) {
      params = params.getReqData();
    }

    return this.api.get("/applications/risks/v2", {
      params: params,
      headers: {
        Permissions: this.permission.getJWT("applications"),
      },
    });
  }

  public getListStudents(params): Observable<any> {
    if (params instanceof SearchResponse) {
      params = params.getReqData();
    }
    return this.api.get("/applications/students", {
      params: params,
      headers: {
        Permissions: this.permission.getJWT("applications"),
      },
    });
  }

  public getListCollections(params): Observable<any> {
    if (params instanceof SearchResponse) {
      params = params.getReqData();
    }
    return this.api.get("/applications/collections", {
      params: params,
      headers: {
        Permissions: this.permission.getJWT("applications"),
      },
    });
  }

  public getListCsr(params): Observable<any> {
    if (params instanceof SearchResponse) {
      params = params.getReqData();
    }
    return this.api.get("/applications/csr", {
      params: params,
      headers: {
        Permissions: this.permission.getJWT("applications"),
      },
    });
  }

  public getOpportunitiesList(params): Observable<any> {
    if (params instanceof SearchResponse) {
      params = params.getReqData();
    }
    return this.api.get("/applications/opportunities/search", {
      params: params,
      headers: {
        Permissions: this.permission.getJWT("applications"),
      },
    });
  }

  public getAdditionalInfo(id): Observable<any> {
    return;
  }

  public deleteApplications(ids): Observable<any> {
    return this.api.post("/applications/delete", {
      body: {
        applications_ids: ids,
      },
      headers: {
        Permissions: this.permission.getJWT("applications"),
      },
    });
  }

  private readonly COLLECTION_COUNTDOWN_STORE_KEY = 'collection_countdown';

  private getMidnightTimestamp(): number {
    const midnight = new Date();
    midnight.setHours(24, 0, 0, 0);
    return midnight.getTime();
  }

  private getCollectionCountDownStore(): Record<string, any> {
    try {
      return JSON.parse(localStorage.getItem(this.COLLECTION_COUNTDOWN_STORE_KEY) || '{}');
    } catch {
      return {};
    }
  }

  private setCollectionCountDownStore(store: Record<string, any>): void {
    try {
      localStorage.setItem(this.COLLECTION_COUNTDOWN_STORE_KEY, JSON.stringify(store));
    } catch { /* ignore */ }
  }

  public getCollectionCountDown(id): Observable<any> {
    const userId = (localStore.get('user') || {}).crm_id || 'unknown';
    const entryKey = `${userId}_${id}`;
    const store = this.getCollectionCountDownStore();
    const cached = store[entryKey];

    if (cached && cached.expires_at > Date.now()) {
      return of(cached.data);
    }

    return this.api.get("/applications/" + id + "/collection_move_time", {
      headers: {
        Permissions: this.permission.getJWT("applications"),
      },
    }).pipe(
      map((res) => Object.assign({}, res, { cached_at: Date.now() })),
      tap((res) => {
        const latest = this.getCollectionCountDownStore();
        latest[entryKey] = { data: res, expires_at: this.getMidnightTimestamp() };
        this.setCollectionCountDownStore(latest);
      })
    );
  }

  public downloadApplication(
    params,
    ids = [],
    type = { result: 0, password: 0 }
  ): Observable<any> {
    if (params instanceof SearchResponse) {
      params = params.getReqData();
    }
    params = Object.assign(params, { password: type.password });
    if (ids.length > 0 && type.result === -1) {
      return this.api.get("/applications?excel=all&excel_users=" + ids, {
        params: params,
        responseType: "text",
      });
    } else if (type.result === 1) {
      return this.api.get("/applications?excel=all", {
        params: params,
        responseType: "text",
      });
    } else if (type.result === 2) {
      return this.api.get("/applications?excel=" + params.page_size, {
        params: params,
        responseType: "text",
      });
    }
  }
}
